import React, { useRef, useState } from 'react';
import {
  View, TouchableOpacity, SectionList, GestureResponderEvent,
} from 'react-native';

import { Inline, Box } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import PortalOverlay, {
  PortalOverlayModal,
} from '../../../ui/PortalOverlay';
import {
  Stack,
  HeaderText,
  ModalBox,
  BodyText,
} from '../../../ui';
import analytics from '../../../../modules/analytics';
import { ChevronIcon } from '../../../ui/Icons';
import { ServiceCategoryPills, ServiceCategoryPillsProps } from './ServiceCategoryPills';
import { ServiceSectionListProps } from './types';
import { ServiceSectionHeader } from './ServiceSectionHeader';

const HiddenSeparator = () => <View style={{ width: '100%', height: 4 }} />;

type ServiceListModalProps = {
  onRequestClose: () => void;
  initialTabIndex?: number;
} & ServiceSectionListProps;

export const ServiceListModal: React.FC<ServiceListModalProps> = ({
  onRequestClose,
  sections,
  initialTabIndex = -1,
  ...restProps
}) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(initialTabIndex);
  const sectionHeaderEls = useRef<Array<HTMLElement>>([]);
  const intersectionObserver = useRef<IntersectionObserver>(null);
  const ioEnabled = useRef<boolean>(false);
  const sectionList = useRef<SectionList>(null);

  const handleTabChange = (newTabIndex: number, { scrolled }: { scrolled?: boolean } = {}) => {
    setActiveTabIndex(newTabIndex);

    // scroll the service list to the right section if we haven't gotten there by scrolling
    if (!scrolled && sectionList.current) {
      const sectionIndex = newTabIndex;
      sectionHeaderEls.current[sectionIndex]?.scrollIntoView();
    }
  };

  const handleIntersection = entries => {
    /**
     * handleIntersection always fires at the creation of the intersection observer, when user
     * interaction hasn't happened yet. This means the first service category will always be
     * selected, regardless of the initialTabIndex value, unless we disable the first pass.
     */
    if (!ioEnabled.current) {
      ioEnabled.current = true;
      return;
    }

    let foundEntry = false;
    entries.forEach(entry => {
      if (entry.isIntersecting && !foundEntry) {
        // figure out the section index for the entry and set our tab index to that
        const { sectionIndex: sectionIndexString } = entry.target.dataset;
        const sectionIndex = Number(sectionIndexString);
        if (!Number.isNaN(sectionIndex)) {
          handleTabChange(sectionIndex, { scrolled: true });
          foundEntry = true;
        }
      }
    });
  };

  const createIntersectionObserver = (listHeaderEl: HTMLElement) => {
    if (typeof window?.IntersectionObserver !== 'undefined') {
      intersectionObserver.current = new IntersectionObserver(handleIntersection, {
        root: listHeaderEl,
        threshold: 1.0,
      });
    }
  };

  const setHeader = (headerEl: HTMLElement) => {
    if (intersectionObserver.current) {
      intersectionObserver.current.disconnect();
    }

    if (headerEl) {
      createIntersectionObserver(headerEl.parentElement.parentElement.parentElement);
    }
  };

  const setSectionHeaderRef = (sectionRef: HTMLElement) => {
    if (intersectionObserver.current && sectionRef) {
      const idx = Number(sectionRef.dataset.sectionIndex);

      const oldRef = sectionHeaderEls.current[idx];

      if (oldRef) {
        // if re-rendering the same index
        intersectionObserver.current.unobserve(oldRef);
      } else if (idx === initialTabIndex) {
        // first render pass AND we're the section corresponding to the initial tab
        sectionRef.scrollIntoView();
      }

      sectionHeaderEls.current[idx] = sectionRef;
      intersectionObserver.current.observe(sectionRef);
    }
  };

  const renderSectionHeader = ({ section: { index, name } }) => {
    const idx = sections.findIndex(listedSection => listedSection.index === index);

    return (!name ? <HiddenSeparator /> : (
      <ServiceSectionHeader>
        <Box paddingLeft={16}>
          <BodyText
            color={colors.blackInBlack}
            weight="thin"
          >
            <span
              ref={setSectionHeaderRef}
              data-section-index={idx}
              style={{
                display: 'flex',
                flexGrow: 1,
                minWidth: 1,
                height: 1,
              }}
            />
            {name}
          </BodyText>
        </Box>
      </ServiceSectionHeader>
    ));
  };

  const renderListHeader = () => (
    <div ref={setHeader} />
  );

  return (
    <PortalOverlay name="service-list-modal" animationStyle="slideLeft">
      <PortalOverlayModal position="fullScreen">
        <Stack padding={16} paddingBottom={4} showBoxShadow>
          <View>
            <TouchableOpacity
              testID="ServicesPopupCloseBtn"
              onPress={(evt: GestureResponderEvent) => {
                analytics.track('clicked_backbutton_seeallservicesoverlay');
                onRequestClose();
                evt.preventDefault();
                evt.stopPropagation();
              }}
            >
              <Inline
                alignItems="center"
                paddingLeft={8}
                paddingTop={4}
                paddingBottom={20}
              >
                <ChevronIcon
                  direction="left"
                  width={16}
                  height={16}
                />
                <BodyText textDecoration="underline">Back</BodyText>
              </Inline>
            </TouchableOpacity>
            <Inline justifyContent="space-between" paddingLeft={8} paddingTop={4}>
              <HeaderText testID="ServicesPopupHeader">Services</HeaderText>
            </Inline>
          </View>
          <ServiceCategoryPills
            inOverlay
            serviceGroups={
              // there is a fix for this casting, but it's in a newer set of typings for react
              // native that seems like it would require an upgrade from 0.61 to 0.63, which
              // could be a whole ordeal
              sections as unknown as ServiceCategoryPillsProps['serviceGroups']
            }
            onTabChange={handleTabChange}
            activeTabIndex={activeTabIndex}
          />
        </Stack>
        <ModalBox padding={0}>
          <SectionList
            ref={sectionList}
            {...restProps}
            sections={sections}
            renderSectionHeader={renderSectionHeader as unknown as ServiceSectionListProps['renderSectionHeader']}
            ListHeaderComponent={renderListHeader}
          />
        </ModalBox>
      </PortalOverlayModal>
    </PortalOverlay>
  );
};
